import type { VehicleColour } from '../../data/vehicles'

/* The paints this car is offered in, as swatches.

   Picking one says which colour the car is wanted in — nothing more. The
   swatches used to be links onto the sister car standing in that paint, so
   touching one re-priced the page; a visitor choosing a colour is not asking
   to be quoted a different plate number, and a price that moves under the hand
   reads as a bait. The pick is carried into the booking instead, stated on
   the form and filed with the order, and the desk confirms the paint when it
   calls.

   Buttons in a radio group, then, rather than links: the thing they do is
   choose, and one exclusive choice out of a few is what a screen reader
   should hear.

   The swatch is the paint itself, so it cannot also carry the selected
   state — 'Pearl White' on a pale page has no edge of its own to thicken.
   The chosen one is ringed instead, and its name spelled out beside the
   legend, so nothing here depends on telling two nearly identical greys
   apart. */

type ColourPickerProps = {
  colours: VehicleColour[]
  /** The id of the paint the visitor has picked. */
  value: string
  onChange: (id: string) => void
  className?: string
}

function ColourPicker({ colours, value, onChange, className = '' }: ColourPickerProps) {
  /* One colour is a fact about the car, not a choice to offer. It is printed
     on the spec sheet like any other, and a picker with a single option in
     it is a control with nothing to do. */
  if (colours.length < 2) return null

  const selected = colours.find((colour) => colour.id === value) ?? colours[0]

  return (
    <div
      role="radiogroup"
      aria-label="Colours this car is offered in"
      className={className}
    >
      <p className="flex flex-wrap items-baseline gap-x-2 label-sm text-dim">
        Colour
        <span className="font-sans text-[13px] tracking-normal text-platinum normal-case">
          {selected.name}
        </span>
      </p>

      <ul className="mt-3 flex flex-wrap items-center gap-3">
        {colours.map((colour) => {
          const current = colour.id === selected.id

          return (
            <li key={colour.id}>
              <button
                type="button"
                role="radio"
                aria-checked={current}
                onClick={() => onChange(colour.id)}
                title={
                  current
                    ? `${colour.name} — chosen`
                    : `${colour.name} — book it in this colour`
                }
                className="group relative block cursor-pointer rounded-full focus:outline-none"
              >
                {/* The ring sits outside the swatch, so it reads on a black
                    paint and a white one alike. */}
                <span
                  aria-hidden="true"
                  className={`block size-9 rounded-full border border-line-soft ring-lime ring-offset-2 ring-offset-ink transition duration-200 group-hover:scale-[1.08] group-focus-visible:ring-2 ${
                    current ? 'ring-2' : ''
                  } ${colour.available ? '' : 'opacity-60'}`}
                  style={{ backgroundColor: colour.hex }}
                />

                <span className="sr-only">
                  {colour.name}
                  {current ? ' — chosen' : ''}
                  {colour.available ? '' : ' — currently on hold'}
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      {/* Said rather than left to be worked out: the price on this page is
          the car's and does not move with the swatch, and the colour picked
          here is the one the booking is filed in. */}
      <p className="mt-3 text-[12.5px] text-dim">
        Pick the paint you want — the price is the same, and your choice goes
        on the booking.
      </p>
    </div>
  )
}

export default ColourPicker
